import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import cspmService from '@/services/cspm.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useInventory';
import { useCspmPermissions } from '@/hooks/useCspmPermissions';
import { ItemSelect, formatCatalogItemDescription, type ItemLike } from '@/components/inventory/ItemSelect';
import warehouseLocationService from '@/services/warehouseLocation.service';
import { CspmSourceDocumentSelect } from '@/components/cspm/CspmSourceDocumentSelect';
import { CspmAllocationTargetSelect } from '@/components/cspm/CspmAllocationTargetSelect';
import { isCspmCreateRoute } from '@/utils/cspmCreateRoute';
import {
  CSPM_ALLOCATION_TARGET_LABELS,
  CSPM_ALLOCATION_TARGET_TYPES,
  CSPM_CLASSIFICATIONS,
  CSPM_CLASSIFICATION_LABELS,
  CSPM_CONDITIONS,
  CSPM_CONDITION_LABELS,
  CSPM_CRITICALITY,
  CSPM_CRITICALITY_LABELS,
  CSPM_DISPOSITION_TYPES,
  CSPM_DISPOSITION_TYPE_LABELS,
  CSPM_EXCEPTION_TYPES,
  CSPM_EXCEPTION_TYPE_LABELS,
  CSPM_PURPOSES,
  CSPM_PURPOSE_LABELS,
  CSPM_SOURCE_TYPES,
  CSPM_SOURCE_TYPE_LABELS,
  enumOptions,
  type CustomerPropertyReceiptLine,
} from '@/constants/cspm';
import { EngineCprPrint, EngineCprLineLabelPrint } from '@/components/print/EngineLetterheadPrints';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import {
  DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_EXCEPTION,
  DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_RECEIPT,
} from '@/constants/document-entity-types';

const { Text } = Typography;

type LineRow = CustomerPropertyReceiptLine & { key: string };

const emptyLine = (): LineRow => ({
  key: `new-${Date.now()}-${Math.random()}`,
  description: '',
  uom: 'PCS',
  classification: 'OTHER',
  purpose: 'FOR_CONSUMPTION',
  criticality: 'NON_CRITICAL',
  condition_code: 'GOOD',
  expected_qty: 0,
  received_qty: 0,
  damaged_qty: 0,
  accepted_qty: 0,
  rejected_qty: 0,
  short_qty: 0,
  excess_qty: 0,
});

const STATUS_COLOR: Record<string, string> = {
  Draft: 'default',
  Posted: 'success',
  Cancelled: 'error',
};

const CustomerPropertyReceiptPage: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  const isNew = isCspmCreateRoute(routeId, location.pathname);
  const id = isNew ? undefined : routeId;
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const cspmPerms = useCspmPermissions();
  const [form] = Form.useForm();
  const [allocForm] = Form.useForm();
  const [transferForm] = Form.useForm();
  const [excForm] = Form.useForm();
  const [dispForm] = Form.useForm();
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [printOpen, setPrintOpen] = useState(false);
  const [labelLine, setLabelLine] = useState<LineRow | null>(null);
  const [allocOpen, setAllocOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState<{
    id: string;
    quantity: number;
    target_document_type?: string;
    target_document_id?: string;
  } | null>(null);
  const [excOpen, setExcOpen] = useState(false);
  const [dispOpen, setDispOpen] = useState<string | null>(null);
  const isUnplanned = Form.useWatch('is_unplanned', form);
  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedSourceType = Form.useWatch('source_document_type', form);
  const watchedWarehouseId = Form.useWatch('warehouse_id', form);
  const allocTargetType = Form.useWatch('target_document_type', allocForm);
  const transferTargetType = Form.useWatch('target_document_type', transferForm);

  const { data: customers = [] } = useActiveCustomers();
  const { data: warehouses = [] } = useWarehouses();
  const { data: warehouseLocations = [] } = useQuery({
    queryKey: ['warehouse-locations', watchedWarehouseId],
    queryFn: () => warehouseLocationService.listByWarehouse(watchedWarehouseId),
    enabled: !!watchedWarehouseId,
  });
  const binOptions = useMemo(
    () =>
      warehouseLocations
        .filter((l) => l.is_active !== false)
        .map((l) => ({
          value: l.id,
          label: `${l.location_code} — ${l.location_name}`,
        })),
    [warehouseLocations]
  );

  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const warehouseOptions = useMemo(
    () =>
      (warehouses as Array<{ id: string; warehouse_name?: string; name?: string }>).map((w) => ({
        value: w.id,
        label: w.warehouse_name || w.name || w.id,
      })),
    [warehouses]
  );

  const { data: receipt, isLoading } = useQuery({
    queryKey: ['cspm', 'receipt', id],
    queryFn: () => cspmService.getReceipt(id!),
    enabled: !isNew && Boolean(id),
  });

  const isDraft = isNew || receipt?.status === 'Draft';

  const { data: openRequirements } = useQuery({
    queryKey: ['cspm', 'requirements', 'open', watchedCustomerId],
    queryFn: () =>
      cspmService.listRequirements({
        customer_id: watchedCustomerId,
        status: 'Open',
        take: 100,
      }),
    enabled: Boolean(watchedCustomerId) && isDraft,
  });

  const requirementOptions = useMemo(
    () =>
      (openRequirements?.rows ?? []).map((r) => ({
        value: r.id,
        label: r.requirement_number || r.id,
      })),
    [openRequirements]
  );

  const { data: allocations = [], refetch: refetchAllocations } = useQuery({
    queryKey: ['cspm', 'allocations', id],
    queryFn: () => cspmService.listAllocationsByReceipt(id!),
    enabled: !isNew && Boolean(id) && receipt?.status === 'Posted',
  });

  const { data: exceptionsData, refetch: refetchExceptions } = useQuery({
    queryKey: ['cspm', 'exceptions', id],
    queryFn: () => cspmService.listExceptions({ receipt_id: id!, take: 50 }),
    enabled: !isNew && Boolean(id) && receipt?.status === 'Posted',
  });

  const customerName =
    customerOptions.find((c) => c.value === (receipt?.customer_id || watchedCustomerId))
      ?.label || receipt?.customer_name;

  useEffect(() => {
    if (!receipt) return;
    form.setFieldsValue({
      customer_id: receipt.customer_id,
      source_document_type: receipt.source_document_type,
      source_document_id: receipt.source_document_id,
      requirement_id: receipt.requirement_id,
      is_unplanned: receipt.is_unplanned,
      unplanned_reason: receipt.unplanned_reason,
      customer_delivery_note: receipt.customer_delivery_note,
      packing_list_ref: receipt.packing_list_ref,
      customer_reference: receipt.customer_reference,
      receipt_datetime: receipt.receipt_datetime ? dayjs(receipt.receipt_datetime) : dayjs(),
      warehouse_id: receipt.warehouse_id,
      vehicle_courier_ref: receipt.vehicle_courier_ref,
      remarks: receipt.remarks,
      inspection_required: receipt.inspection_required,
    });
    setLines(
      (receipt.lines || []).map((ln, idx) => ({
        ...ln,
        key: ln.id || `line-${idx}`,
      }))
    );
  }, [receipt, form]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['cspm'] });
  };

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.createReceipt(body),
    onSuccess: (row) => {
      message.success('Receipt draft created');
      invalidate();
      if (row?.id) navigate(`/cspm/receipts/${row.id}`, { replace: true });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.updateReceipt(id!, body),
    onSuccess: () => {
      message.success('Saved');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const postMut = useMutation({
    mutationFn: () => cspmService.postReceipt(id!),
    onSuccess: () => {
      message.success('Receipt posted');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Post failed'),
  });

  const cancelMut = useMutation({
    mutationFn: () => cspmService.cancelReceipt(id!),
    onSuccess: () => {
      message.success('Cancelled');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Cancel failed'),
  });

  const releaseMut = useMutation({
    mutationFn: () => cspmService.releaseReceipt(id!),
    onSuccess: () => {
      message.success('Released');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Release failed'),
  });

  const failMut = useMutation({
    mutationFn: (remarks?: string) => cspmService.failReceipt(id!, { remarks }),
    onSuccess: () => {
      message.success('Inspection failed');
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Fail failed'),
  });

  const allocateMut = useMutation({
    mutationFn: (body: {
      receipt_line_id: string;
      quantity: number;
      target_document_type: string;
      target_document_id: string;
      remarks?: string;
    }) => cspmService.allocate(body),
    onSuccess: () => {
      message.success('Allocated');
      setAllocOpen(false);
      allocForm.resetFields();
      refetchAllocations();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Allocate failed'),
  });

  const transferMut = useMutation({
    mutationFn: ({
      allocationId,
      body,
    }: {
      allocationId: string;
      body: {
        quantity?: number;
        target_document_type: string;
        target_document_id: string;
        remarks?: string;
      };
    }) => cspmService.transferAllocation(allocationId, body),
    onSuccess: () => {
      message.success('Allocation transferred');
      setTransferOpen(null);
      transferForm.resetFields();
      refetchAllocations();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Transfer failed'),
  });

  const exceptionMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.createException(body),
    onSuccess: () => {
      message.success('Exception recorded');
      setExcOpen(false);
      excForm.resetFields();
      refetchExceptions();
      invalidate();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Exception failed'),
  });

  const dispositionMut = useMutation({
    mutationFn: ({
      exceptionId,
      body,
    }: {
      exceptionId: string;
      body: Record<string, unknown>;
    }) => cspmService.addDisposition(exceptionId, body),
    onSuccess: () => {
      message.success('Disposition recorded');
      setDispOpen(null);
      dispForm.resetFields();
      refetchExceptions();
      invalidate();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Disposition failed'),
  });

  const createNcrMut = useMutation({
    mutationFn: (exceptionId: string) => cspmService.createNcrFromException(exceptionId),
    onSuccess: (data) => {
      message.success('NCR created from exception');
      refetchExceptions();
      invalidate();
      const ncrId = data?.ncr?.id;
      if (ncrId) navigate(`/qms/ncrs/${ncrId}`);
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'NCR create failed'),
  });

  const closeExceptionMut = useMutation({
    mutationFn: (exceptionId: string) => cspmService.closeException(exceptionId),
    onSuccess: () => {
      message.success('Exception closed');
      refetchExceptions();
      invalidate();
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Close failed'),
  });

  const buildBody = async () => {
    const values = await form.validateFields();
    const payloadLines = lines
      .map((ln) => ({
        requirement_line_id: ln.requirement_line_id || null,
        classification: ln.classification,
        purpose: ln.purpose,
        criticality: ln.criticality,
        item_id: ln.item_id || null,
        customer_item_ref: ln.customer_item_ref || null,
        description: String(ln.description || '').trim(),
        uom: ln.uom || 'PCS',
        expected_qty: Number(ln.expected_qty) || 0,
        received_qty: Number(ln.received_qty) || 0,
        damaged_qty: Number(ln.damaged_qty) || 0,
        accepted_qty: Number(ln.accepted_qty) || 0,
        rejected_qty: Number(ln.rejected_qty) || 0,
        short_qty: Number(ln.short_qty) || 0,
        excess_qty: Number(ln.excess_qty) || 0,
        condition_code: ln.condition_code || 'GOOD',
        condition_notes: ln.condition_notes?.trim() || null,
        manufacturer: ln.manufacturer || null,
        model: ln.model || null,
        serial_number: ln.serial_number || null,
        batch_number: ln.batch_number || null,
        lot_number: ln.lot_number || null,
        heat_number: ln.heat_number || null,
        remarks: ln.remarks || null,
        storage_requirement: ln.storage_requirement || null,
        location_id: ln.location_id || null,
        expiry_date: ln.expiry_date
          ? dayjs(ln.expiry_date).format('YYYY-MM-DD')
          : null,
      }))
      .filter((ln) => ln.description);
    if (!payloadLines.length) throw new Error('At least one line with a description is required');
    return {
      ...values,
      receipt_datetime: values.receipt_datetime
        ? dayjs(values.receipt_datetime).toISOString()
        : undefined,
      lines: payloadLines,
    };
  };

  const onSave = async () => {
    try {
      const body = await buildBody();
      if (isNew) createMut.mutate(body);
      else updateMut.mutate(body);
    } catch (e: any) {
      message.error(e?.message || 'Please complete required fields');
    }
  };

  const updateLine = (key: string, patch: Partial<LineRow>) => {
    setLines((prev) => prev.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)));
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const lineSelectOptions = (receipt?.lines || lines)
    .filter((ln) => ln.id)
    .map((ln) => ({
      value: ln.id!,
      label: `#${ln.line_number ?? ''} ${ln.description}`.trim(),
    }));

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cspm')}>
                Back
              </Button>
              <div>
                <h2 style={{ margin: 0 }}>
                  {isNew ? 'New externally owned property receipt' : receipt?.receipt_number || 'EOP'}
                </h2>
                {!isNew && receipt?.status && (
                  <Space size={4}>
                    <Tag color={STATUS_COLOR[receipt.status] || 'default'}>{receipt.status}</Tag>
                    {receipt.inspection_status && <Tag>{receipt.inspection_status}</Tag>}
                    {receipt.is_unplanned && <Tag color="orange">Unplanned</Tag>}
                  </Space>
                )}
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {!isNew && (
                <>
                  <Button onClick={() => navigate(`/cspm/receipts/${id}/passport`)}>Passport</Button>
                  <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
                    Print
                  </Button>
                </>
              )}
              {isDraft &&
                ((isNew && cspmPerms.canReceiptCreate) || (!isNew && cspmPerms.canReceiptEdit)) && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={createMut.isPending || updateMut.isPending}
                  onClick={onSave}
                >
                  Save
                </Button>
              )}
              {!isNew && receipt?.status === 'Draft' && (
                <>
                  {cspmPerms.canReceiptPost && (
                    <Button
                      type="primary"
                      loading={postMut.isPending}
                      onClick={() =>
                        modal.confirm({
                          title: 'Post this receipt?',
                          content: 'Qty truth becomes immutable and stock may post.',
                          onOk: () => postMut.mutateAsync(),
                        })
                      }
                    >
                      Post
                    </Button>
                  )}
                  {cspmPerms.canReceiptEdit && (
                    <Button
                      danger
                      icon={<StopOutlined />}
                      loading={cancelMut.isPending}
                      onClick={() =>
                        modal.confirm({
                          title: 'Cancel draft receipt?',
                          onOk: () => cancelMut.mutateAsync(),
                        })
                      }
                    >
                      Cancel
                    </Button>
                  )}
                </>
              )}
              {!isNew &&
                receipt?.status === 'Posted' &&
                receipt.inspection_status === 'Pending' &&
                cspmPerms.canInspect && (
                <>
                  {receipt.inspection_record_id ? (
                    <Button onClick={() => navigate(`/qms/inspections/${receipt.inspection_record_id}`)}>
                      Open QC inspection
                    </Button>
                  ) : null}
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={releaseMut.isPending}
                    onClick={() => releaseMut.mutate()}
                  >
                    Release
                  </Button>
                  <Button
                    danger
                    icon={<WarningOutlined />}
                    loading={failMut.isPending}
                    onClick={() =>
                      modal.confirm({
                        title: 'Fail inspection?',
                        onOk: () => failMut.mutateAsync('Inspection failed'),
                      })
                    }
                  >
                    Fail quality
                  </Button>
                </>
              )}
              {!isNew &&
                receipt?.status === 'Posted' &&
                receipt.inspection_record_id &&
                receipt.inspection_status !== 'Pending' && (
                  <Button onClick={() => navigate(`/qms/inspections/${receipt.inspection_record_id}`)}>
                    View QC inspection
                  </Button>
                )}
            </Space>
          </Col>
        </Row>

        <Card title="Header">
          <Form
            form={form}
            layout="vertical"
            disabled={!isDraft}
            initialValues={{ receipt_datetime: dayjs(), is_unplanned: false, inspection_required: false }}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="customer_id" label="Customer" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="label" options={customerOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="source_document_type" label="Source type" rules={[{ required: true }]}>
                  <Select
                    options={enumOptions(CSPM_SOURCE_TYPES, CSPM_SOURCE_TYPE_LABELS)}
                    onChange={() => form.setFieldsValue({ source_document_id: undefined })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="source_document_id" label="Source document" rules={[{ required: true }]}>
                  <CspmSourceDocumentSelect
                    sourceType={watchedSourceType}
                    customerId={watchedCustomerId}
                    onValidated={(result) => {
                      if (result?.customer_id) {
                        form.setFieldsValue({ customer_id: result.customer_id });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="warehouse_id" label="Warehouse" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={warehouseOptions}
                    onChange={() =>
                      setLines((prev) => prev.map((l) => ({ ...l, location_id: null })))
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="receipt_datetime" label="Receipt datetime" rules={[{ required: true }]}>
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="requirement_id" label="Planned requirement">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Optional open requirement"
                    options={requirementOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="customer_delivery_note" label="Customer DN">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="packing_list_ref" label="Packing list ref">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="customer_reference" label="Customer reference">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="vehicle_courier_ref" label="Vehicle / courier">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="is_unplanned" valuePropName="checked">
                  <Checkbox>Unplanned receipt</Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="inspection_required" valuePropName="checked">
                  <Checkbox>Inspection required</Checkbox>
                </Form.Item>
              </Col>
              {isUnplanned && (
                <Col xs={24} md={16}>
                  <Form.Item
                    name="unplanned_reason"
                    label="Unplanned reason"
                    rules={[{ required: true, message: 'Reason required for unplanned' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card
          title="Qty truth lines"
          extra={
            isDraft ? (
              <Button icon={<PlusOutlined />} onClick={() => setLines((p) => [...p, emptyLine()])}>
                Add line
              </Button>
            ) : null
          }
        >
          {isDraft ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {lines.map((row, idx) => (
                <Card
                  key={row.key}
                  size="small"
                  type="inner"
                  title={`Line ${idx + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={lines.length <= 1}
                      onClick={() => setLines((prev) => prev.filter((l) => l.key !== row.key))}
                    >
                      Remove
                    </Button>
                  }
                >
                  <Row gutter={[16, 12]}>
                    <Col xs={24} lg={8}>
                      <Text type="secondary">Item (optional)</Text>
                      <ItemSelect
                        value={row.item_id || undefined}
                        allowClear
                        style={{ width: '100%', marginTop: 4 }}
                        placeholder="Link inventory item"
                        onChange={(itemId, option) => {
                          const fromOpt = (option as { item?: ItemLike } | undefined)?.item;
                          updateLine(row.key, {
                            item_id: itemId || null,
                            description:
                              row.description?.trim() ||
                              (fromOpt ? formatCatalogItemDescription(fromOpt) : row.description),
                            uom: fromOpt?.base_uom || row.uom || 'PCS',
                          });
                        }}
                      />
                    </Col>
                    <Col xs={24} lg={16}>
                      <Text type="secondary">Description</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        value={row.description}
                        placeholder="What arrived"
                        onChange={(e) => updateLine(row.key, { description: e.target.value })}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Text type="secondary">Condition</Text>
                      <Select
                        size="large"
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.condition_code}
                        options={enumOptions(CSPM_CONDITIONS, CSPM_CONDITION_LABELS)}
                        onChange={(condition_code) => updateLine(row.key, { condition_code })}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Text type="secondary">Classification</Text>
                      <Select
                        size="large"
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.classification}
                        options={enumOptions(CSPM_CLASSIFICATIONS, CSPM_CLASSIFICATION_LABELS)}
                        onChange={(classification) => updateLine(row.key, { classification })}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Text type="secondary">Purpose</Text>
                      <Select
                        size="large"
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.purpose}
                        options={enumOptions(CSPM_PURPOSES, CSPM_PURPOSE_LABELS)}
                        onChange={(purpose) => updateLine(row.key, { purpose })}
                      />
                    </Col>
                    <Col xs={12} md={6}>
                      <Text type="secondary">Criticality</Text>
                      <Select
                        size="large"
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.criticality}
                        options={enumOptions(CSPM_CRITICALITY, CSPM_CRITICALITY_LABELS)}
                        onChange={(criticality) => updateLine(row.key, { criticality })}
                      />
                    </Col>
                    <Col xs={24}>
                      <Text type="secondary">Condition note</Text>
                      <Input.TextArea
                        style={{ marginTop: 4 }}
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        maxLength={2000}
                        value={row.condition_notes ?? ''}
                        placeholder="Describe the condition on receipt, e.g. scratches on flange face, thread damage, missing caps"
                        onChange={(e) => updateLine(row.key, { condition_notes: e.target.value })}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: '16px 0 12px' }}>Quantity truth</Divider>
                  <Row gutter={[16, 12]}>
                    {(
                      [
                        ['expected_qty', 'Expected'],
                        ['received_qty', 'Received'],
                        ['accepted_qty', 'Accepted'],
                        ['damaged_qty', 'Damaged'],
                        ['rejected_qty', 'Rejected'],
                        ['short_qty', 'Short'],
                        ['excess_qty', 'Excess'],
                      ] as const
                    ).map(([field, label]) => (
                      <Col xs={12} sm={8} md={6} lg={3} key={field}>
                        <Text type="secondary">{label}</Text>
                        <InputNumber
                          size="large"
                          min={0}
                          style={{ width: '100%', marginTop: 4 }}
                          value={row[field]}
                          onChange={(v) => updateLine(row.key, { [field]: Number(v) || 0 })}
                        />
                      </Col>
                    ))}
                    <Col xs={12} sm={8} md={6} lg={3}>
                      <Text type="secondary">UoM</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        value={row.uom || 'PCS'}
                        onChange={(e) => updateLine(row.key, { uom: e.target.value || 'PCS' })}
                      />
                    </Col>
                  </Row>

                  <Divider style={{ margin: '16px 0 12px' }}>Identity</Divider>
                  <Row gutter={[16, 12]}>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Serial no.</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        value={row.serial_number || ''}
                        onChange={(e) => updateLine(row.key, { serial_number: e.target.value })}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Heat no.</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        value={row.heat_number || ''}
                        onChange={(e) => updateLine(row.key, { heat_number: e.target.value })}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Lot / batch</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        value={row.lot_number || row.batch_number || ''}
                        onChange={(e) =>
                          updateLine(row.key, {
                            lot_number: e.target.value,
                            batch_number: e.target.value,
                          })
                        }
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Expiry date</Text>
                      <DatePicker
                        size="large"
                        style={{ width: '100%', marginTop: 4 }}
                        value={row.expiry_date ? dayjs(row.expiry_date) : null}
                        onChange={(d) =>
                          updateLine(row.key, {
                            expiry_date: d ? d.format('YYYY-MM-DD') : null,
                          })
                        }
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Bin (optional)</Text>
                      <Select
                        size="large"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        style={{ width: '100%', marginTop: 4 }}
                        disabled={!watchedWarehouseId}
                        options={binOptions}
                        value={row.location_id || undefined}
                        placeholder={
                          watchedWarehouseId ? 'Warehouse bin' : 'Select warehouse first'
                        }
                        onChange={(v) => updateLine(row.key, { location_id: v || null })}
                      />
                    </Col>
                    <Col xs={24} md={8}>
                      <Text type="secondary">Storage requirement</Text>
                      <Input
                        size="large"
                        style={{ marginTop: 4 }}
                        placeholder="e.g. cool dry / climate controlled"
                        value={row.storage_requirement || ''}
                        onChange={(e) =>
                          updateLine(row.key, { storage_requirement: e.target.value })
                        }
                      />
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          ) : (
            <Table
              rowKey="key"
              dataSource={lines}
              pagination={false}
              scroll={{ x: 1100 }}
              size="middle"
              columns={[
                {
                  title: '#',
                  width: 48,
                  render: (_: unknown, __: LineRow, idx: number) => idx + 1,
                },
                {
                  title: 'Description',
                  dataIndex: 'description',
                  ellipsis: true,
                },
                {
                  title: 'Condition',
                  width: 180,
                  render: (_: unknown, row: LineRow) => (
                    <>
                      {CSPM_CONDITION_LABELS[row.condition_code as keyof typeof CSPM_CONDITION_LABELS] ||
                        row.condition_code ||
                        '—'}
                      {row.condition_notes ? (
                        <div>
                          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                            {row.condition_notes}
                          </Text>
                        </div>
                      ) : null}
                    </>
                  ),
                },
                {
                  title: 'Class',
                  width: 120,
                  render: (_: unknown, row: LineRow) =>
                    CSPM_CLASSIFICATION_LABELS[
                      row.classification as keyof typeof CSPM_CLASSIFICATION_LABELS
                    ] ||
                    row.classification ||
                    '—',
                },
                { title: 'Exp', dataIndex: 'expected_qty', width: 80 },
                { title: 'Rcv', dataIndex: 'received_qty', width: 80 },
                { title: 'Acc', dataIndex: 'accepted_qty', width: 80 },
                { title: 'Dmg', dataIndex: 'damaged_qty', width: 80 },
                { title: 'Rej', dataIndex: 'rejected_qty', width: 80 },
                {
                  title: 'S/N',
                  width: 120,
                  render: (_: unknown, row: LineRow) => row.serial_number || '—',
                },
                {
                  title: 'Heat',
                  width: 100,
                  render: (_: unknown, row: LineRow) => row.heat_number || '—',
                },
                {
                  title: 'Quality',
                  width: 110,
                  render: (_: unknown, row: LineRow) => row.quality_status || '—',
                },
                {
                  title: 'Custody',
                  width: 120,
                  render: (_: unknown, row: LineRow) => row.custody_status || '—',
                },
                {
                  title: '',
                  width: 100,
                  render: (_: unknown, row: LineRow) => (
                    <Button type="link" size="small" onClick={() => setLabelLine(row)}>
                      Label
                    </Button>
                  ),
                },
              ]}
            />
          )}
        </Card>

        {!isNew && receipt?.status === 'Posted' && (
          <Card
            title="Allocations"
            extra={
              cspmPerms.canAllocate ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAllocOpen(true)}>
                  Allocate
                </Button>
              ) : null
            }
          >
            <Table
              rowKey="id"
              dataSource={allocations}
              pagination={false}
              locale={{ emptyText: 'No allocations yet' }}
              columns={[
                { title: 'Line', dataIndex: 'receipt_line_id', ellipsis: true },
                { title: 'Qty', dataIndex: 'quantity', width: 90 },
                {
                  title: 'Target type',
                  dataIndex: 'target_document_type',
                  width: 140,
                  render: (v: string) =>
                    CSPM_ALLOCATION_TARGET_LABELS[
                      v as keyof typeof CSPM_ALLOCATION_TARGET_LABELS
                    ] || v,
                },
                { title: 'Target id', dataIndex: 'target_document_id', ellipsis: true },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 120,
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: '',
                  width: 100,
                  render: (
                    _: unknown,
                    row: {
                      id: string;
                      quantity: number;
                      status: string;
                      target_document_type?: string;
                      target_document_id?: string;
                    }
                  ) =>
                    row.status === 'Open' || row.status === 'PartiallyIssued' ? (
                      cspmPerms.canTransfer ? (
                      <Button
                        type="link"
                        onClick={() => {
                          transferForm.setFieldsValue({
                            quantity: row.quantity,
                            target_document_type: row.target_document_type,
                            target_document_id: undefined,
                            remarks: undefined,
                          });
                          setTransferOpen({
                            id: row.id,
                            quantity: Number(row.quantity) || 0,
                            target_document_type: row.target_document_type,
                            target_document_id: row.target_document_id,
                          });
                        }}
                      >
                        Transfer
                      </Button>
                      ) : null
                    ) : null,
                },
              ]}
            />
          </Card>
        )}

        {!isNew && receipt?.status === 'Posted' && (
          <Card
            title="Exceptions"
            extra={
              cspmPerms.canExceptionCreate ? (
              <Button
                icon={<WarningOutlined />}
                onClick={() => {
                  excForm.setFieldsValue({
                    customer_id: receipt.customer_id,
                    receipt_id: receipt.id,
                    quantity: 0,
                    exception_type: 'DAMAGED',
                  });
                  setExcOpen(true);
                }}
              >
                Record exception
              </Button>
              ) : null
            }
          >
            <Table
              rowKey="id"
              dataSource={exceptionsData?.rows ?? []}
              pagination={false}
              locale={{ emptyText: 'No exceptions' }}
              expandable={{
                expandedRowRender: (row: {
                  id: string;
                  status?: string;
                  notification_ref?: string | null;
                  ncr_id?: string | null;
                }) => (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">
                      Customer notify:{' '}
                      {row.notification_ref ? <Tag color="blue">{row.notification_ref}</Tag> : '—'}
                    </Text>
                    <EntityAttachmentsCard
                      entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_EXCEPTION}
                      entityId={row.id}
                      title="Exception evidence"
                      readOnly={false}
                      uploadHint="Photos / customer correspondence for this exception."
                    />
                  </Space>
                ),
              }}
              columns={[
                { title: 'Number', dataIndex: 'exception_number', width: 140 },
                {
                  title: 'Type',
                  dataIndex: 'exception_type',
                  width: 140,
                  render: (v: string) =>
                    CSPM_EXCEPTION_TYPE_LABELS[v as keyof typeof CSPM_EXCEPTION_TYPE_LABELS] || v,
                },
                { title: 'Qty', dataIndex: 'quantity', width: 90 },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  width: 120,
                  render: (v: string) => <Tag color={v === 'Open' ? 'orange' : 'default'}>{v}</Tag>,
                },
                {
                  title: 'Notify',
                  dataIndex: 'notification_ref',
                  width: 150,
                  render: (v: string | null) => (v ? <Tag>{v}</Tag> : '—'),
                },
                {
                  title: 'NCR',
                  dataIndex: 'ncr_id',
                  width: 100,
                  render: (v: string | null) =>
                    v ? <Link to={`/qms/ncrs/${v}`}>Open</Link> : '—',
                },
                {
                  title: '',
                  width: 280,
                  render: (
                    _: unknown,
                    row: {
                      id: string;
                      status?: string;
                      notification_ref?: string | null;
                      ncr_id?: string | null;
                    }
                  ) => (
                    <Space wrap>
                      {(row.status === 'Open' || row.status === 'UnderDisposition') &&
                      cspmPerms.canDisposition ? (
                        <Button
                          type="link"
                          onClick={() => {
                            dispForm.setFieldsValue({
                              quantity: 0,
                              disposition_type: 'RETURN_TO_CUSTOMER',
                              close: false,
                            });
                            setDispOpen(row.id);
                          }}
                        >
                          Disposition
                        </Button>
                      ) : null}
                      {row.status === 'UnderDisposition' && cspmPerms.canDisposition ? (
                        <Button
                          type="link"
                          loading={closeExceptionMut.isPending}
                          onClick={() =>
                            modal.confirm({
                              title: 'Close this exception?',
                              onOk: () => closeExceptionMut.mutateAsync(row.id),
                            })
                          }
                        >
                          Close
                        </Button>
                      ) : null}
                      {!row.ncr_id && cspmPerms.canExceptionCreate ? (
                        <Button
                          type="link"
                          loading={createNcrMut.isPending}
                          onClick={() =>
                            modal.confirm({
                              title: 'Create QMS NCR from this exception?',
                              onOk: () => createNcrMut.mutateAsync(row.id),
                            })
                          }
                        >
                          Create NCR
                        </Button>
                      ) : null}
                    </Space>
                  ),
                },
              ]}
            />
          </Card>
        )}

        <EntityAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_RECEIPT}
          entityId={isNew ? null : id}
          title="Evidence / attachments"
          readOnly={!isDraft && receipt?.status !== 'Posted'}
          saveFirstMessage="Save the receipt draft before attaching files."
          uploadHint="Photos, packing lists, certificates — PDF or images preferred."
        />
      </Space>

      <Modal
        title="Allocate externally owned property"
        open={allocOpen}
        onCancel={() => setAllocOpen(false)}
        onOk={() => allocForm.submit()}
        confirmLoading={allocateMut.isPending}
        destroyOnClose
      >
        <Form
          form={allocForm}
          layout="vertical"
          onFinish={(values) => allocateMut.mutate(values)}
        >
          <Form.Item name="receipt_line_id" label="Receipt line" rules={[{ required: true }]}>
            <Select options={lineSelectOptions} />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0.0001} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="target_document_type"
            label="Target document type"
            rules={[{ required: true }]}
          >
            <Select
              options={enumOptions(CSPM_ALLOCATION_TARGET_TYPES, CSPM_ALLOCATION_TARGET_LABELS)}
              placeholder="Select target"
              onChange={() => allocForm.setFieldsValue({ target_document_id: undefined })}
            />
          </Form.Item>
          <Form.Item name="target_document_id" label="Target document" rules={[{ required: true }]}>
            <CspmAllocationTargetSelect
              targetType={allocTargetType}
              customerId={receipt?.customer_id || watchedCustomerId}
            />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        <Divider />
        <Text type="secondary">Allocation is only allowed after the receipt is posted.</Text>
      </Modal>

      <Modal
        title="Transfer allocation"
        open={!!transferOpen}
        onCancel={() => {
          setTransferOpen(null);
          transferForm.resetFields();
        }}
        onOk={() => transferForm.submit()}
        confirmLoading={transferMut.isPending}
        destroyOnClose
      >
        <Form
          form={transferForm}
          layout="vertical"
          onFinish={(values) => {
            if (!transferOpen) return;
            transferMut.mutate({ allocationId: transferOpen.id, body: values });
          }}
        >
          <Form.Item name="quantity" label="Quantity to transfer">
            <InputNumber
              min={0.0001}
              max={transferOpen?.quantity || undefined}
              style={{ width: '100%' }}
              placeholder={`Max ${transferOpen?.quantity ?? ''}`}
            />
          </Form.Item>
          <Form.Item
            name="target_document_type"
            label="New target type"
            rules={[{ required: true }]}
          >
            <Select
              options={enumOptions(CSPM_ALLOCATION_TARGET_TYPES, CSPM_ALLOCATION_TARGET_LABELS)}
              onChange={() => transferForm.setFieldsValue({ target_document_id: undefined })}
            />
          </Form.Item>
          <Form.Item
            name="target_document_id"
            label="New target document"
            rules={[{ required: true }]}
          >
            <CspmAllocationTargetSelect
              targetType={transferTargetType}
              customerId={receipt?.customer_id || watchedCustomerId}
            />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Record exception"
        open={excOpen}
        onCancel={() => setExcOpen(false)}
        onOk={() => excForm.submit()}
        confirmLoading={exceptionMut.isPending}
        destroyOnClose
      >
        <Form
          form={excForm}
          layout="vertical"
          onFinish={(values) =>
            exceptionMut.mutate({
              ...values,
              customer_id: receipt?.customer_id,
              receipt_id: receipt?.id,
            })
          }
        >
          <Form.Item name="exception_type" label="Type" rules={[{ required: true }]}>
            <Select options={enumOptions(CSPM_EXCEPTION_TYPES, CSPM_EXCEPTION_TYPE_LABELS)} />
          </Form.Item>
          <Form.Item name="receipt_line_id" label="Receipt line (optional)">
            <Select allowClear options={lineSelectOptions} />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="condition_code" label="Condition">
            <Select allowClear options={enumOptions(CSPM_CONDITIONS, CSPM_CONDITION_LABELS)} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="ncr_id" label="NCR id (optional)">
            <Input placeholder="Link existing QMS NCR" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Authorize disposition"
        open={Boolean(dispOpen)}
        onCancel={() => setDispOpen(null)}
        onOk={() => dispForm.submit()}
        confirmLoading={dispositionMut.isPending}
        destroyOnClose
      >
        <Form
          form={dispForm}
          layout="vertical"
          onFinish={(values) => {
            if (!dispOpen) return;
            dispositionMut.mutate({ exceptionId: dispOpen, body: values });
          }}
        >
          <Form.Item name="disposition_type" label="Disposition" rules={[{ required: true }]}>
            <Select options={enumOptions(CSPM_DISPOSITION_TYPES, CSPM_DISPOSITION_TYPE_LABELS)} />
          </Form.Item>
          <Form.Item name="quantity" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="authorization_ref" label="Authorization reference">
            <Input />
          </Form.Item>
          <Form.Item name="authorized_by_customer" label="Customer authorized" valuePropName="checked">
            <Checkbox />
          </Form.Item>
          <Form.Item name="close" valuePropName="checked">
            <Checkbox>Close exception after disposition</Checkbox>
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        {receipt && (
          <EngineCprPrint
            data={receipt}
            customerName={customerName}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </Modal>

      <Modal
        open={Boolean(labelLine)}
        onCancel={() => setLabelLine(null)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        {receipt && labelLine && (
          <EngineCprLineLabelPrint
            receipt={receipt}
            line={labelLine}
            customerName={customerName}
            onClose={() => setLabelLine(null)}
          />
        )}
      </Modal>
    </div>
  );
};

export default CustomerPropertyReceiptPage;
